// Study Hall socket (DESIGN.md 11, 14 and 15). The token goes in the first frame (`hello`), never in a
// URL. Ops that carry a ref `r` resolve on the frame answering that ref (`ack`, or `err` to reject);
// everything else goes to onEvent. Heartbeat: an app `ping` every 25 s, and a socket with nothing
// inbound for 65 s is treated as dead. Reconnects use full-jitter backoff, or the server's
// `bye.retry` window when it said goodbye first. An abnormal loss after ready spreads the first
// retry over 1–15 s too, so a server crash cannot synchronize every client into a reconnect burst.
// Never retry after 4001, 4003, 4009 or 4010.
// After three failed first connects (never opened on this page) the counts-only `ws_blocked`
// beacon goes out once; the first successful open sends `ws_ok` once.

export const PROTO = 1;
export const NO_RETRY = new Set([4001, 4003, 4009, 4010]);
export const PING_MS = 25000;
export const SILENT_MS = 65000;
export const REQUEST_MS = 10000;

// Full jitter: rand(0, min(30 s, 1 s * 2^n)).
export function backoffMs(n, rand = Math.random) { return Math.floor(rand() * Math.min(30000, 1000 * 2 ** Math.max(0, n))); }

// The first delay after the server closed us, or null to fall back to backoff. 1012/1013 without a
// `bye` default to 1 to 15 s (spreads a school behind one IP); an op flood (4029) waits 60 s.
export function byeDelayMs(bye, code, rand = Math.random) {
	if (code === 4029) return 60000;
	let r = bye && Array.isArray(bye.retry) && bye.retry.length === 2 ? bye.retry.map(Number) : null;
	if (r && !(Number.isFinite(r[0]) && Number.isFinite(r[1]))) r = null;
	if (!r && (code === 1012 || code === 1013)) r = [1, 15];
	if (!r) return null;
	const lo = Math.max(0, Math.min(r[0], r[1])), hi = Math.min(600, Math.max(r[0], r[1]));
	return Math.round((lo + rand() * Math.max(0, hi - lo)) * 1000);
}

export function createConn({
	url, getToken, getHello, onEvent, onState, beacon,
	WS = globalThis.WebSocket, now = Date.now, rand = Math.random,
	timers = { setTimeout: (f, ms) => setTimeout(f, ms), clearTimeout: (t) => clearTimeout(t), setInterval: (f, ms) => setInterval(f, ms), clearInterval: (t) => clearInterval(t) },
}) {
	let ws = null, want = false, attempt = 0, everOpen = false, firstFails = 0, sentBlocked = false;
	let lastIn = 0, pingT = 0, watchT = 0, retryT = 0, bye = null, seq = 0, state = "idle";
	let retryNotBefore = 0, ready = false, silentMs = SILENT_MS;
	const waits = new Map();

	function setState(s, info) { state = s; if (onState) onState(s, info || {}); }
	function raw(frame) { if (ws && ws.readyState === 1) { ws.send(JSON.stringify(frame)); return true; } return false; }

	function failAll(code) {
		for (const [r, w] of waits) { timers.clearTimeout(w.timer); w.reject({ code, r }); }
		waits.clear();
	}
	function stopBeat() { if (pingT) timers.clearInterval(pingT); if (watchT) timers.clearInterval(watchT); pingT = watchT = 0; }
	function beat() {
		stopBeat();
		pingT = timers.setInterval(() => raw({ t: "ping", d: {} }), PING_MS);
		watchT = timers.setInterval(() => {
			if (ws && now() - lastIn > silentMs) { const w = ws; unhook(); try { w.close(1000); } catch (e) { void e; } closed(1006, true); }
		}, 5000);
	}
	function unhook() {
		if (!ws) return;
		ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
		ws = null;
	}

	function open() {
		bye = null;
		ready = false;
		retryNotBefore = 0;
		let w;
		try { w = new WS(url); } catch (e) { void e; closed(1006, false); return; }
		ws = w;
		let didOpen = false;
		setState("connecting");
		w.onopen = () => {
			if (ws !== w) return;
			didOpen = true;
			lastIn = now();
			const hello = { tok: String(getToken() || ""), v: PROTO, ...(getHello ? getHello() : {}) };
			raw({ t: "hello", d: hello });
			beat();
			if (!everOpen && beacon) beacon("ws_ok");
			everOpen = true;
			setState("open");
		};
		w.onmessage = (ev) => {
			if (ws !== w) return;
			lastIn = now();
			let f = null;
			try { f = JSON.parse(ev.data); } catch (e) { void e; return; }
			if (f && typeof f.t === "string") frame(f);
		};
		w.onclose = (ev) => { if (ws !== w) return; unhook(); closed(ev && ev.code ? ev.code : 1006, didOpen); };
		w.onerror = () => {}; // a close event always follows
	}

	function frame(f) {
		const d = f.d && typeof f.d === "object" ? f.d : {};
		if (f.t === "bye") bye = d;
		if (f.t === "ready") { attempt = 0; ready = true; }
		const r = typeof f.r === "string" ? f.r : "";
		if (r && waits.has(r) && f.t !== "bye") {
			const w = waits.get(r);
			waits.delete(r);
			timers.clearTimeout(w.timer);
			if (f.t === "err") w.reject({ ...d, code: d.code || "internal", r }); else w.resolve(d);
			if (f.t === "ack" || f.t === "err") return; // a ready/hist answering a ref still goes to onEvent
		}
		if (f.t === "pong") return;
		if (onEvent) onEvent(f.t, d, r);
	}

	function closed(code, didOpen) {
		const wasReady = ready;
		ready = false;
		stopBeat();
		failAll("disconnected");
		if (!didOpen && !everOpen) {
			firstFails++;
			if (firstFails >= 3 && !sentBlocked && beacon) { sentBlocked = true; beacon("ws_blocked"); }
		}
		const info = { code, bye, didOpen, firstFails, everOpen };
		if (!want) { setState("stopped", info); return; }
		if (NO_RETRY.has(code)) { want = false; setState("closed", info); return; }
		let delay = byeDelayMs(bye, code, rand);
		if (delay === null && code === 1006 && wasReady) delay = byeDelayMs(null, 1012, rand);
		retryNotBefore = delay === null ? 0 : now() + delay;
		if (delay === null) delay = backoffMs(attempt, rand);
		attempt = Math.min(attempt + 1, 8);
		setState("retrying", { ...info, delay });
		retryT = timers.setTimeout(() => { retryT = 0; if (want && !ws) open(); }, delay);
	}

	return {
		start() { want = true; if (retryT) { timers.clearTimeout(retryT); retryT = 0; } if (!ws) open(); },
		// Closes on purpose (hidden too long, gate cards, sign-out); nothing reconnects until start().
		stop(code = 1000) {
			want = false;
			if (retryT) { timers.clearTimeout(retryT); retryT = 0; }
			const w = ws;
			unhook();
			stopBeat();
			failAll("disconnected");
			if (w) { try { w.close(code); } catch (e) { void e; } }
			setState("stopped", {});
		},
		// Spread tabs coming online behind the same school IP. A visibility/network event
		// never shortens a server/crash recovery window or resets the accumulated failure backoff.
		kick() {
			if (!want || ws) return;
			if (retryT) timers.clearTimeout(retryT);
			const delay = Math.max(Math.floor(rand() * 2000), retryNotBefore - now());
			retryT = timers.setTimeout(() => { retryT = 0; if (want && !ws) open(); }, delay);
		},
		request(t, d, { timeoutMs = REQUEST_MS } = {}) {
			return new Promise((resolve, reject) => {
				if (!ws || ws.readyState !== 1) { reject({ code: "disconnected" }); return; }
				const r = (++seq).toString(36);
				const timer = timers.setTimeout(() => { waits.delete(r); reject({ code: "timeout", r }); }, timeoutMs);
				waits.set(r, { resolve, reject, timer });
				raw({ t, r, d: d || {} });
			});
		},
		fire(t, d) { return raw({ t, d: d || {} }); },
		isOpen: () => !!ws && ws.readyState === 1,
		// A hidden tab's timers can run once a minute: allow longer silence there (0: the default).
		setSilentMs(ms) { silentMs = ms > 0 ? ms : SILENT_MS; },
		state: () => state,
	};
}
